<template>
  <Teleport to="body">
    <!-- Баннер согласия. Аналитика и виджеты поддержки не грузятся, пока
         человек не сделал выбор, — поэтому баннер показывается до них, а не
         рядом с ними. -->
    <Transition name="cookie-slide">
      <div v-if="showBanner" class="cookie-notice" data-test="cookie-banner">
        <!-- «по правилам» — ссылка на политику: там перечислено, какие
             именно cookie мы храним и зачем. Ссылка внутри предложения, а не
             строкой ниже: её читают вместе с фразой, которую она объясняет. -->
        <!-- eslint-disable-next-line vue/no-v-html -->
        <p class="cookie-notice__text" v-html="noticeHtml" />

        <div class="cookie-notice__actions">
          <button
            class="cookie-notice__btn"
            data-test="cookie-accept-required"
            @click="acceptRequired"
          >
            {{ t('cookie.gotIt') }}
          </button>

          <button
            class="cookie-notice__settings"
            data-test="cookie-open-settings"
            @click="openSettings"
          >
            {{ t('cookie.customize') }}
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>

  <!-- Настройки категорий: те же две необязательные категории, что в
       документе `/law/services`. Обязательные показаны, но не выключаются.
       Окно телепортируется само — в общий Teleport баннера его класть незачем. -->
  <BaseModal
    :open="showSettingsModal"
    :title="t('cookie.modalTitle')"
    size="lg"
    data-test="cookie-settings"
    @close="closeSettings"
  >
    <div class="cookie-modal">
      <h2 class="cookie-modal__title">{{ t('cookie.modalTitle') }}</h2>

      <div class="cookie-modal__categories">
        <div class="cookie-category">
          <div class="cookie-category__header">
            <span class="cookie-category__name">{{ t('cookie.essential.name') }}</span>
            <div class="cookie-category__lock">
              <svg width="20"
height="20"
viewBox="0 0 24 24"
fill="none">
                <rect x="3"
y="11"
width="18"
height="11"
rx="2"
stroke="currentColor"
stroke-width="2" />
                <path
                  d="M7 11V7C7 4.23858 9.23858 2 12 2C14.7614 2 17 4.23858 17 7V11"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            </div>
          </div>
          <p class="cookie-category__description">{{ t('cookie.essential.description') }}</p>
        </div>

        <div class="cookie-category">
          <div class="cookie-category__header">
            <span class="cookie-category__name">{{ t('cookie.analytics.name') }}</span>
            <label class="cookie-toggle">
              <input
                v-model="preferences.analytics"
                type="checkbox"
                class="cookie-toggle__input"
                data-test="cookie-toggle-analytics"
              />
              <span class="cookie-toggle__slider" />
            </label>
          </div>
          <p class="cookie-category__description">{{ t('cookie.analytics.description') }}</p>
        </div>

        <div class="cookie-category">
          <div class="cookie-category__header">
            <span class="cookie-category__name">{{ t('cookie.support.name') }}</span>
            <label class="cookie-toggle">
              <input
                v-model="preferences.support"
                type="checkbox"
                class="cookie-toggle__input"
                data-test="cookie-toggle-support"
              />
              <span class="cookie-toggle__slider" />
            </label>
          </div>
          <p class="cookie-category__description">{{ t('cookie.support.description') }}</p>
        </div>
      </div>

      <div class="cookie-modal__actions">
        <button class="cookie-modal__btn cookie-modal__btn--secondary" @click="acceptRequired">
          {{ t('cookie.acceptRequired') }}
        </button>
        <button class="cookie-modal__btn cookie-modal__btn--primary" @click="savePreferences">
          {{ t('cookie.accept') }}
        </button>
      </div>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
/**
 * Баннер согласия igra.now.
 *
 * Категорий ровно две необязательные — аналитика и поддержка с диагностикой,
 * — и они дословно те же, что в документе `/law/services`: расходиться этим
 * двум спискам нельзя, иначе баннер спрашивает про одно, а документ обещает
 * другое. Рекламных и трекинговых cookie у сервиса нет, поэтому категории под
 * них здесь не заводится: пустой переключатель — обещание, которого никто не
 * давал.
 *
 * Ни один необязательный переключатель не включён заранее: предустановленное
 * согласие согласием не считается. Пока выбор не сделан, `hasConsent()` для
 * обеих категорий возвращает false, и потребители (Sentry, счётчик, виджет
 * поддержки) не грузятся.
 */
import { computed, inject, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import BaseModal from '@/shared/components/base/BaseModal.vue'
import { legalUrl } from '@/modules/legal/legalDocs'
import { consentDecided, readConsent, writeConsent } from '@/shared/consent'

const { t } = useI18n()

const showBanner = ref(false)
const showSettingsModal = ref(false)

/**
 * Внутри Telegram (мини-апп) уведомление о cookie не показываем — решение
 * оператора 2026-09-18: экран мини-аппа маленький, а человек уже внутри
 * приложения, которому доверился. Выбор при этом за него не делаем:
 * необязательные категории остаются выключенными, настройки по-прежнему
 * доступны по ссылке в футере.
 */
const isInTelegram = inject<boolean>('isInTelegram', false)

/**
 * Адрес по умолчанию абсолютный: уведомление живёт и на публичной точке входа
 * без роутера. i5g.org передаёт свою политику (`/law/privacy` своего домена).
 */
const props = withDefaults(defineProps<{ privacyUrl?: string }>(), {
  privacyUrl: () => legalUrl('privacy'),
})

/**
 * Текст уведомления со ссылкой внутри предложения. Собирается здесь, а не в
 * шаблоне, чтобы адрес политики не размазывался по вёрстке и в локали лежала
 * цельная фраза с одним подставляемым куском.
 */
const noticeHtml = computed(() =>
  t('cookie.notice', {
    rules: `<a href="${props.privacyUrl}" target="_blank" rel="noopener" class="cookie-notice__link">${t('cookie.noticeRules')}</a>`,
  }),
)

const preferences = reactive({ analytics: false, support: false })

function persist(choice: { analytics: boolean; support: boolean }) {
  writeConsent(choice)
  preferences.analytics = choice.analytics
  preferences.support = choice.support
  showBanner.value = false
  showSettingsModal.value = false
}

/**
 * Кнопка «Ясно». Это подтверждение того, что человек уведомление прочитал, —
 * выбор оно за него не делает: необязательные категории остаются выключенными,
 * пока он сам не включит их в настройках. Согласие по-прежнему предшествует
 * загрузке аналитики и чата, как того требует HK-IGRA.127; уведомление лишь
 * перестало загораживать экран тремя кнопками.
 */
function acceptRequired() {
  persist({ analytics: false, support: false })
}

function savePreferences() {
  persist({ analytics: preferences.analytics, support: preferences.support })
}

function openSettings() {
  showSettingsModal.value = true
}

function closeSettings() {
  showSettingsModal.value = false
}

/** Точка входа для ссылки «Настройки согласия» в футере. */
defineExpose({ openSettings })

onMounted(() => {
  const stored = readConsent()
  if (stored !== null) {
    preferences.analytics = stored.analytics
    preferences.support = stored.support
  }
  showBanner.value = !isInTelegram && !consentDecided()

  window.addEventListener('igra-consent-open-settings', openSettings)
})
</script>

<style scoped lang="scss">
// Animations
.cookie-slide-enter-active,
.cookie-slide-leave-active {
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease;
}

.cookie-slide-enter-from,
.cookie-slide-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

// Cookie Banner
// Уведомление живёт в углу и НЕ имеет затемняющей подложки (решение оператора
// 2026-09-03): это уведомление, а не запрос решения, — оно не вправе гасить
// страницу и перехватывать клики. Карточка позиционируется сама, без обёртки
// на весь экран: полноэкранный слой ловил бы клики даже прозрачным.
.cookie-notice {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 9999;
  width: min(400px, calc(100vw - 40px));
  padding: 16px 18px;
  border: 1px solid var(--hm-border);
  border-radius: var(--p-radius-20);
  background: var(--hm-surface);
  box-shadow: var(--hm-shadow-soft);

  // Прижатая к низу полоса на телефоне: во всю ширину с малыми отступами и
  // минимальной высотой, чтобы не съедать экран лендинга.
  @media (max-width: 768px) {
    right: 12px;
    left: 12px;
    bottom: 12px;
    width: auto;
    padding: 12px 14px;
  }

  &__text {
    margin: 0 0 12px;
    color: var(--ds-text-secondary);
    font-size: var(--ds-text-size-caption);
    line-height: 1.45;
  }

  :deep(.cookie-notice__link) {
    color: var(--ds-accent);
    text-decoration: underline;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  &__btn {
    padding: 9px 22px;
    border: 0;
    border-radius: var(--ds-radius-control);
    background: var(--ds-accent);
    color: var(--ds-text-on-accent);
    font-family: inherit;
    font-size: var(--ds-text-size-caption);
    font-weight: var(--ds-weight-semibold);
    cursor: pointer;
  }

  // Управление аналитикой остаётся на виду: уведомление не отменяет выбора,
  // оно только перестаёт им загораживать экран.
  &__settings {
    padding: 6px 0;
    border: 0;
    background: none;
    color: var(--ds-text-muted);
    font-family: inherit;
    font-size: var(--ds-text-size-caption);
    text-decoration: underline;
    cursor: pointer;

    &:hover {
      color: var(--ds-text-secondary);
    }
  }
}

.cookie-modal {
  &__title {
    font-size: var(--p-text-22);
    font-weight: var(--ds-weight-bold);
    color: var(--hm-text-primary);
    margin: 0 0 28px;
    text-align: center;

    @media (max-width: 768px) {
      font-size: var(--p-text-18);
      margin: 0 0 20px;
    }
  }

  &__categories {
    display: flex;
    flex-direction: column;
    gap: 24px;
    margin-bottom: 32px;

    @media (max-width: 768px) {
      gap: 20px;
      margin-bottom: 24px;
    }
  }

  &__actions {
    display: flex;
    gap: 16px;

    @media (max-width: 768px) {
      gap: 12px;
    }

    @media (max-width: 480px) {
      flex-direction: column;
    }
  }

  &__btn {
    flex: 1;
    padding: 16px 28px;
    border-radius: var(--p-radius-50);
    font-size: var(--p-text-15);
    font-weight: var(--ds-weight-semibold);
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;

    @media (max-width: 768px) {
      padding: 14px 20px;
      font-size: var(--ds-text-size-body);
    }

    &--primary {
      background: linear-gradient(135deg, var(--hm-accent-light) 0%, var(--hm-accent) 100%);
      color: var(--hm-text-primary);
      box-shadow: 0 4px 16px var(--ds-accent-soft-30);

      &:hover {
        transform: translateY(-1px);
        box-shadow: 0 6px 20px var(--ds-accent-soft-40);
      }
    }

    &--secondary {
      background: var(--hm-surface);
      color: var(--hm-text-primary);
      border: 1.5px solid var(--hm-border);

      &:hover {
        background: var(--hm-surface-alt);
      }
    }
  }
}

// Cookie Category
.cookie-category {
  padding-bottom: 20px;
  border-bottom: 1px solid var(--hm-border);

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }

  &__name {
    font-size: var(--ds-text-size-body-lg);
    font-weight: var(--ds-weight-semibold);
    color: var(--hm-text-primary);
  }

  &__lock {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--hm-surface-alt);
    border-radius: var(--ds-radius-control);
    color: var(--hm-text-muted);
  }

  &__description {
    font-size: var(--p-text-13);
    line-height: 1.6;
    color: var(--hm-text-secondary);
    margin: 0;
  }
}

// Toggle Switch
.cookie-toggle {
  position: relative;
  width: 52px;
  height: 28px;
  display: inline-block;

  &__input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;

    &:checked + .cookie-toggle__slider {
      background: linear-gradient(135deg, var(--hm-accent-light) 0%, var(--hm-accent) 100%);

      &::before {
        transform: translateX(24px);
      }
    }

    &:focus + .cookie-toggle__slider {
      box-shadow: 0 0 0 3px var(--ds-accent-soft-20);
    }
  }

  &__slider {
    position: absolute;
    inset: 0;
    background: var(--hm-border);
    border-radius: var(--p-radius-28);
    cursor: pointer;
    transition: all 0.3s ease;

    &::before {
      content: '';
      position: absolute;
      top: 2px;
      left: 2px;
      width: 24px;
      height: 24px;
      background: var(--hm-text-primary);
      border-radius: 50%;
      box-shadow: 0 2px 6px var(--ds-scrim);
      transition: transform 0.3s ease;
    }
  }
}
</style>
